import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { AddressSuggestion } from '../core/domain/address';
import { BanAddressService } from '../core/geo/ban-address.service';
import { GeolocationService } from '../core/geo/geolocation.service';
import { CtButton } from './button.directive';
import { CtField } from './field';

const SEARCH_DELAY_MS = 300;

/**
 * Bloc d'adresse postale : saisie libre, suggestions BAN, et préremplissage GPS.
 *
 * Réutilisé à l'inscription (champs facultatifs) et sur le profil. Le GPS ne
 * part que sur clic explicite — même règle que sur l'accueil. La suggestion
 * GPS est une approximation : l'utilisateur peut la corriger avant d'enregistrer.
 */
@Component({
  selector: 'ct-address-fields',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, CtButton, CtField],
  template: `
    <div class="ct-stack">
      <div class="locate">
        <p class="ct-muted note">
          {{
            hint() ||
              'Saisissez votre adresse ou laissez le GPS la proposer. Vous pourrez corriger avant d’enregistrer.'
          }}
        </p>
        @if (geoStatus() !== 'unsupported') {
          <button
            type="button"
            ctButton="secondary"
            [disabled]="locating()"
            (click)="fillFromGps()"
          >
            {{ locating() ? 'Localisation…' : 'Utiliser ma position' }}
          </button>
        }
      </div>

      @if (gpsNotice(); as message) {
        <p class="ct-muted small" role="status">{{ message }}</p>
      }
      @if (gpsError(); as message) {
        <p class="ct-error small" role="alert">{{ message }}</p>
      }

      <div class="suggest">
        <ct-field label="Adresse" [hint]="optional() ? 'Facultatif' : undefined">
          <input
            name="street"
            type="text"
            autocomplete="street-address"
            [(ngModel)]="street"
            (ngModelChange)="onStreetChange($event)"
            (focus)="showSuggestions.set(true)"
          />
        </ct-field>

        @if (showSuggestions() && suggestions().length > 0) {
          <ul class="list" role="listbox">
            @for (item of suggestions(); track item.label) {
              <li>
                <button type="button" role="option" (click)="pick(item)">
                  {{ item.label }}
                </button>
              </li>
            }
          </ul>
        }
      </div>

      <ct-field
        label="Complément d’adresse"
        [hint]="
          optional() ? 'Facultatif — bâtiment, étage, digicode…' : 'Bâtiment, étage, digicode…'
        "
      >
        <input
          name="addressLine2"
          type="text"
          autocomplete="address-line2"
          [(ngModel)]="addressLine2"
        />
      </ct-field>

      <div class="row">
        <ct-field label="Code postal" [hint]="optional() ? 'Facultatif' : undefined">
          <input
            name="postalCode"
            type="text"
            inputmode="numeric"
            autocomplete="postal-code"
            maxlength="5"
            [(ngModel)]="postalCode"
          />
        </ct-field>

        <ct-field label="Commune" [hint]="optional() ? 'Facultatif' : undefined">
          <input name="commune" type="text" autocomplete="address-level2" [(ngModel)]="commune" />
        </ct-field>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      padding-top: var(--ct-space-3);
      border-top: 1px dashed var(--ct-border);
    }

    .locate {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--ct-space-2);
    }

    .note {
      font-size: var(--ct-size-sm);
      flex: 1;
      min-width: 12rem;
      margin: 0;
    }

    .small {
      font-size: var(--ct-size-sm);
      margin: 0;
    }

    .suggest {
      position: relative;
    }

    .list {
      position: absolute;
      z-index: 4;
      top: calc(100% - 0.25rem);
      left: 0;
      right: 0;
      margin: 0;
      padding: var(--ct-space-1);
      list-style: none;
      background: var(--ct-white);
      border: 1px solid var(--ct-border);
      border-radius: var(--ct-radius-md);
      box-shadow: 0 0.5rem 1.25rem rgb(61 46 34 / 12%);
      max-height: 14rem;
      overflow: auto;
    }

    .list button {
      display: block;
      width: 100%;
      padding: var(--ct-space-2) var(--ct-space-3);
      border: 0;
      border-radius: var(--ct-radius-sm);
      background: transparent;
      color: var(--ct-text);
      font: inherit;
      font-size: var(--ct-size-sm);
      text-align: left;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background: var(--ct-kraft);
        outline: none;
      }
    }

    .row {
      display: grid;
      grid-template-columns: minmax(6.5rem, 8.5rem) 1fr;
      gap: var(--ct-space-3);
      align-items: start;
    }

    @media (width < 28rem) {
      .row {
        grid-template-columns: 1fr;
      }
    }
  `,
})
export class CtAddressFields {
  private readonly ban = inject(BanAddressService);
  private readonly geolocation = inject(GeolocationService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  /** True à l'inscription : les libellés rappellent que tout est facultatif. */
  readonly optional = input(false);
  readonly hint = input<string | undefined>(undefined);

  readonly street = model('');
  readonly addressLine2 = model('');
  readonly postalCode = model('');
  readonly commune = model('');

  protected readonly geoStatus = this.geolocation.status;
  protected readonly suggestions = signal<readonly AddressSuggestion[]>([]);
  protected readonly showSuggestions = signal(false);
  protected readonly locating = signal(false);
  protected readonly gpsNotice = signal<string | undefined>(undefined);
  protected readonly gpsError = signal<string | undefined>(undefined);

  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  private searchGeneration = 0;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.searchTimer !== undefined) {
        clearTimeout(this.searchTimer);
      }
    });
  }

  protected onStreetChange(value: string): void {
    this.showSuggestions.set(true);
    this.gpsNotice.set(undefined);
    this.gpsError.set(undefined);
    if (this.searchTimer !== undefined) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      void this.search(value);
    }, SEARCH_DELAY_MS);
  }

  protected pick(item: AddressSuggestion): void {
    this.apply(item);
    this.suggestions.set([]);
    this.showSuggestions.set(false);
  }

  protected async fillFromGps(): Promise<void> {
    if (this.locating()) {
      return;
    }
    this.locating.set(true);
    this.gpsNotice.set(undefined);
    this.gpsError.set(undefined);
    this.suggestions.set([]);
    this.showSuggestions.set(false);

    try {
      const position = await this.geolocation.requestPosition();
      const address = await this.ban.reverse(position);
      if (address === undefined) {
        this.gpsError.set('Aucune adresse trouvée près de votre position.');
        return;
      }
      this.apply(address);
      this.gpsNotice.set(
        'Adresse proposée à partir de votre position — vérifiez-la avant d’enregistrer.',
      );
    } catch (error) {
      this.gpsError.set(describeGpsError(error));
    } finally {
      this.locating.set(false);
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  protected onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target;
    if (target instanceof Node && !this.host.nativeElement.contains(target)) {
      this.showSuggestions.set(false);
    }
  }

  private apply(item: AddressSuggestion): void {
    this.street.set(item.street);
    this.postalCode.set(item.postalCode);
    this.commune.set(item.commune);
  }

  private async search(query: string): Promise<void> {
    const generation = ++this.searchGeneration;
    try {
      const results = await this.ban.search(query);
      if (generation !== this.searchGeneration) {
        return;
      }
      this.suggestions.set(results);
    } catch {
      if (generation !== this.searchGeneration) {
        return;
      }
      this.suggestions.set([]);
    }
  }
}

function describeGpsError(error: unknown): string {
  if (isGeolocationPositionError(error)) {
    if (error.code === error.PERMISSION_DENIED) {
      return 'Autorisation de localisation refusée.';
    }
    return 'Impossible d’obtenir votre position.';
  }
  if (error instanceof Error && error.message === 'unsupported') {
    return 'La géolocalisation n’est pas disponible sur cet appareil.';
  }
  return 'Impossible de proposer une adresse pour le moment.';
}

function isGeolocationPositionError(error: unknown): error is GeolocationPositionError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as GeolocationPositionError).code === 'number' &&
    'PERMISSION_DENIED' in error
  );
}
